import {DEFAULT_LINE_PLOT_NUM_RUNS_NEW} from '../../components/WorkspaceDrawer/Settings/runLinePlots/maxRunDefaults';
import {
  type QueryToRunsDataQueryParams,
  type RunsDataQueryConfig,
  toRunsDataQueryConfig,
} from '../../containers/RunsDataLoader';
import {CHART_SAMPLES} from '../../util/constants';
import * as Filters from '../../util/filters';
import {type Filter} from '../../util/filterTypes';
import {createPreloadableLazyComponent} from '../../util/lazyImport';
import {
  WANDB_BBOX_CLASS_LABEL_KEY,
  WANDB_MASK_CLASS_LABEL_KEY,
} from '../../util/mediaConstants';
import type {PanelSpec} from '../../util/panelTypes';
import {type Query} from '../../util/queryTypes';
import {isAnyRunsetGrouped} from '../../util/runhelpers';
import {type Key} from '../../util/runTypes';
import {panelViews} from '../PanelBank/types';
import {type MediaBrowserPanelConfig} from './common';
import {getModeConfig} from './modes/getModeConfig';
import {getTitleFromConfig} from './utils/getTitleFromConfig';
import {includeRequiredRunQueries} from './utils/includeRequiredRunQueries';
export type {MediaBrowserPanelConfig};
export type {Axis, NonEmptyTile} from './common';
export {rowsWithKeysMemo} from './common';

function transformQuery(
  query: Query,
  config: MediaBrowserPanelConfig
): RunsDataQueryConfig {
  const mediaSliderKey = config.mediaSliderKey || '_step';
  const queryToDataQueryParams: QueryToRunsDataQueryParams = {
    selectionsAsFilters: true,
  };

  const modeConfig = getModeConfig(config.mode);
  const runFilterMediaKeys = modeConfig.getRunFilterMediaKeys(config);
  const filterBaseRunsWithMediaKey = modeConfig.shouldFilterRunsInQuery(config);

  // Only fetch runs that logged the selected media key(s), then apply maxRuns
  // (same pattern as line plots). Mode configs decide when to filter and which
  // keys to use; compare keeps unfiltered runs by default so missing keys show
  // per-tile errors, except when run controls are individual.
  //
  // The keys_info "!= null" filter means "this run logged this key". We OR multiple
  // keys so a run matches if it logged any selected key. Without this, the query
  // returns the first N workspace runs regardless of key availability, which can
  // show "No data available" even when later runs do have media.
  if (
    filterBaseRunsWithMediaKey &&
    runFilterMediaKeys != null &&
    runFilterMediaKeys.length > 0
  ) {
    const filters: Array<Filter<Key>> = runFilterMediaKeys.map(mediaKey => ({
      key: {section: 'keys_info', name: mediaKey},
      op: '!=',
      value: null,
    }));
    queryToDataQueryParams.mergeFilters = Filters.Or(filters);
  }

  const result = toRunsDataQueryConfig(query, queryToDataQueryParams, {
    panelType: 'Media Browser',
  });
  result.history = true;

  const requiredRunNames = modeConfig.getRequiredRunNames(config);
  includeRequiredRunQueries(result, requiredRunNames);

  // This is required to get _wandb for single runs
  result.wandbKeys = [WANDB_MASK_CLASS_LABEL_KEY, WANDB_BBOX_CLASS_LABEL_KEY];
  //  This is required to get _wandb for multi runs
  // result.configKeys = ['_wandb'];
  const historySpecs = modeConfig.getHistorySpecs(
    config,
    mediaSliderKey,
    CHART_SAMPLES
  );
  if (historySpecs != null) {
    result.historySpecs = historySpecs;
  }
  result.page = {
    size: config.maxRuns ?? DEFAULT_LINE_PLOT_NUM_RUNS_NEW,
  };

  if (isAnyRunsetGrouped(query.runSets ?? [])) {
    // We need the metadata for grouping because we do it locally
    // TODO: move grouping to server
    // result.disableMeta = false;

    // Disable grouping for this query, we'll do it ourselves.
    result.runsetQueries = result.runsetQueries.map(q => ({
      ...q,
      grouping: [],
    }));
  }

  return result;
}

export const MEDIA_SETTINGS_OVERLAY_NUM_RUNS = 100;

/**
 * Runs query for the workspace- and section-level media settings drawers.
 *
 * Those drawers only need `_wandb` mask/bounding-box class labels to decide
 * which overlay settings to show, so this skips the per-media-key `keys_info`
 * filter and history specs `transformQuery` builds, and the per-run basic,
 * system-metrics and tag fragments. With every media key in a project as
 * input, that filter alone can exceed ClickHouse's query size limit.
 */
export function transformOverlayQuery(query: Query): RunsDataQueryConfig {
  const result = toRunsDataQueryConfig(
    query,
    {selectionsAsFilters: true},
    {panelType: 'Media Browser'}
  );
  result.wandbKeys = [WANDB_MASK_CLASS_LABEL_KEY, WANDB_BBOX_CLASS_LABEL_KEY];
  result.enableBasic = false;
  result.enableSystemMetrics = false;
  result.enableTags = false;
  result.page = {size: MEDIA_SETTINGS_OVERLAY_NUM_RUNS};

  if (isAnyRunsetGrouped(query.runSets ?? [])) {
    result.runsetQueries = result.runsetQueries.map(q => ({
      ...q,
      grouping: [],
    }));
  }

  return result;
}

export const Spec: PanelSpec<
  typeof panelViews.mediaBrowser,
  MediaBrowserPanelConfig
> = {
  type: panelViews.mediaBrowser,
  Component: createPreloadableLazyComponent(
    () => import('./MediaBrowserPanelWrapper')
  ).Component,
  transformQuery,
  getTitleFromConfig,

  configSpec: {
    chartTitle: {editor: 'string', displayName: 'Chart title'},
    columnCount: {
      editor: 'slider',
      displayName: 'Columns',
      min: 1,
      max: 20,
      step: 1,
      default: 4,
    },
    actualSize: {
      editor: 'checkbox',
      displayName: 'Original size',
      default: false,
    },
    fitToDimension: {
      editor: 'checkbox',
      displayName: 'Cover fit',
      default: false,
    },
    pixelated: {editor: 'checkbox', displayName: 'Pixelated', default: true},
    mode: {
      editor: 'dropdown',
      displayName: 'Display as',
      default: 'gallery',
      options: [
        {text: 'Gallery', value: 'gallery'},
        {text: 'Grid', value: 'grid'},
      ],
    },
  },

  icon: 'panel-images',
};
